"use client";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/** Local dev only: set NEXT_PUBLIC_DEV_FORCE_SUBSCRIBED=true in .env */
export function isDevForceSubscribed(): boolean {
  return process.env.NEXT_PUBLIC_DEV_FORCE_SUBSCRIBED === "true";
}

type SubscriptionContextValue = {
  activeSubscription: boolean | null;
  setActiveSubscription: (value: boolean | null) => void;
};

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [activeSubscription, setActiveSubscriptionState] = useState<boolean | null>(
    isDevForceSubscribed() ? true : null
  );

  const setActiveSubscription = useCallback((value: boolean | null) => {
    if (isDevForceSubscribed()) {
      setActiveSubscriptionState(true);
      return;
    }
    setActiveSubscriptionState(value);
  }, []);

  useEffect(() => {
    if (isDevForceSubscribed()) {
      setActiveSubscriptionState(true);
    }
  }, []);

  const value = useMemo(
    () => ({ activeSubscription, setActiveSubscription }),
    [activeSubscription, setActiveSubscription]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return ctx;
}
